import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListProductUseCase } from "../../../Usecase/Products/list-product-usecase";
import { useToast } from "../../../Hooks/ToasContext";
import { IExportProductPayload, IProductEntity, IProductsListPayload } from "../../../Services/api/products/products.interface";
import { GridColDef } from "@mui/x-data-grid";
import { FormatMoney } from "../../../utils/formatMoney";
import { useForm } from "react-hook-form";
import { DestroyProductUseCase } from "../../../Usecase/Products/destroy-product-usecase";
import { TCategoryStore } from "../../../Services/api/categories/categories.interface";
import { ExportExcelProductUseCase } from "../../../Usecase/Products/export-excel-product-usecase";
import { downloadBlob } from "../../../utils/downloadBlob";
import { TStoreType } from "../../../Services/api/service-orders/service-order.interface";
import {
  buildProductsListSearchParams,
  buildProductsListUrl,
  parseProductsListParams,
} from "../../../utils/products-list-url";

interface IUseListProductsProps {
    listProductUseCase: ListProductUseCase
    destroyProductUseCase: DestroyProductUseCase
    exportExcelProductUseCase: ExportExcelProductUseCase
}

export function useListProducts({ listProductUseCase, destroyProductUseCase, exportExcelProductUseCase }: IUseListProductsProps){
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [data, setData] = useState<IProductEntity[]>([])
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [showFiltersModal, setShowFiltersModal] = useState(false)
    const [itemToDestroy, setItemToDestroy] = useState<IProductEntity>()
    const [currentFilters, setCurrentFilters] = useState<IProductsListPayload>(() =>
      parseProductsListParams(searchParams)
    )

    const { control } = useForm()
    const { showToast } =  useToast()

    const syncUrl = useCallback((filters: IProductsListPayload) => {
      setSearchParams(buildProductsListSearchParams(filters), { replace: true });
    }, [setSearchParams]);

    const listProdcuts = useCallback(async (payload: Partial<IProductsListPayload> & { page: number }) => {
        try {
          setLoading(true)
          setLoadError(false)

          const filters: IProductsListPayload = {
            page: payload.page,
            searchBy: payload.searchBy !== undefined ? payload.searchBy : currentFilters.searchBy,
            store: Object.prototype.hasOwnProperty.call(payload, 'store')
                    ? payload.store
                    : currentFilters.store,
            brand: payload.brand !== undefined ? payload.brand : currentFilters.brand,
            isActive: payload.isActive !== undefined ? payload.isActive : currentFilters.isActive ?? true,
          }

          setCurrentFilters(filters)
          syncUrl(filters)

          const listResult = await listProductUseCase.handle(filters);

          if (listResult.isFailure) {
            setLoadError(true)
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

         const list = listResult.getValue()

         if(list){
            setData(list.data)
            setTotalRegisters(list.total)
            setLoadError(false)
         }
        } catch (error) {
          setLoadError(true)
          console.error("error listProducts function: ", error)
        } finally {
          setLoading(false);
        }
      }, [currentFilters, listProductUseCase, showToast, syncUrl]);

    useEffect(() => {
      const filters = parseProductsListParams(searchParams);
      setCurrentFilters(filters);
      listProdcuts(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

      const handlePageChangeProducts = (data: IProductsListPayload) => {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          document.documentElement.scrollTop = 0
          document.body.scrollTop = 0
        } catch (error) {
          console.log('Erro ao fazer scroll:', error)
        }

        listProdcuts({
          page: data.page,
          searchBy: data.searchBy !== undefined ? data.searchBy : currentFilters.searchBy,
        })
    }

      const handleExportExcel = async () => {
        try {
          setLoading(true)

          const filters : IExportProductPayload = {
            searchBy: currentFilters.searchBy,
            store: currentFilters.store as TStoreType,
            brand: currentFilters.brand
          }

          const blobResult = await exportExcelProductUseCase.handle(filters);

          if (blobResult.isFailure) {
            showToast(String(blobResult?.error?.error || 'Erro ao exportar Excel'), 'error');
            return;
          }

          const blob = blobResult.getValue()

         if(blob){
            downloadBlob(blob, 'products.xlsx')
         }

        } catch (error) {
          console.log("error listProducts function: ", error)
        } finally {
          setLoading(false);
        }
      };

    const list = useMemo(() => {
        const rows = data?.map(product => {
            return {
                id: product.id,
                name: product.name,
                code: product.code,
                internalName: product?.internalName || "Não informado",
                price: FormatMoney(Number(product?.price)) || 'R$ 0.00',
                photo: product?.images[0]?.link || 'https://acdn.mitiendanube.com/stores/002/907/105/themes/common/logo-1336738559-1706047471-a90c2b04f7208c4f190adf866d8df0b51706047472-320-0.webp',
                b2bNuvemShopPrice: FormatMoney(Number(product?.b2bNuvemShopPrice)) || 'R$ 0.00',
                b2cNuvemShopPrice: FormatMoney(Number(product?.b2cNuvemShopPrice)) || 'R$ 0.00',
            }
        })

        return rows
    }, [data])

    const handleDestroyProduct = async () => {
      try {
        setLoading(true)

        const listResult = await destroyProductUseCase.handle(itemToDestroy?.id || '');

        if (listResult.isFailure) {
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

        setItemToDestroy(undefined)
        showToast('Removido com sucesso.', 'success');
        await listProdcuts({ page: currentFilters.page })

      } catch (error) {
        console.log("error listProducts function: ", error)
      } finally {
        setLoading(false);
      }
    }

    const handleFilterSelect = (payload: IProductsListPayload) => {
      listProdcuts({ ...currentFilters, ...payload, page: 1 })
    }

    const handleSearch = useCallback(({ page, searchBy }: IProductsListPayload) => {
      listProdcuts({ ...currentFilters, page, searchBy: searchBy ?? '' })
    }, [currentFilters, listProdcuts]);

    const listReturnUrl = useMemo(() => buildProductsListUrl(currentFilters), [currentFilters]);

    return {
        loading,
        loadError,
        listProdcuts,
        list,
        data,
        totalRegisters,
        control,
        showFiltersModal,
        setShowFiltersModal,
        setItemToDestroy,
        itemToDestroy,
        handleDestroyProduct,
        handleFilterSelect,
        currentFilters,
        handleExportExcel,
        handlePageChangeProducts,
        handleSearch,
        listReturnUrl,
    }
}
